import React, { useMemo, useId, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { addDays, startOfWeek } from 'date-fns';
import {
  Scheduler,
  DayView,
  WeekView,
  WorkWeekView,
  MonthView,
  SchedulerItem,
  SchedulerSlot,
  type SchedulerItemProps,
  type SchedulerSlotProps,
  type SchedulerSlotMouseEvent,
  type SchedulerDataChangeEvent,
  type SchedulerDateChangeEvent,
  type SchedulerViewChangeEvent,
  type SchedulerResource,
  type SchedulerGroup,
  type DateHeaderCellProps,
} from '@progress/kendo-react-scheduler';
import { loadMessages, LocalizationProvider } from '@progress/kendo-react-intl';
import { useSchedule } from '../../context/ScheduleContext';
import { AgendaView } from './AgendaView';
import { toKendoItem, fromKendoItem, detectChangeKind, blankKendoItem, type KendoItem } from '../../utils/kendoAdapter';
import { CustomEditForm } from './CustomEditForm';
import { timeToMinutes, getTimezoneAbbr, formatTimeLabel, resolveTimezone } from '../../utils/dateUtils';
import type { BackgroundEvent, EventMovedPayload, EventResizedPayload, Resource, TimeRange, SecondaryTimezone, TimeFormat } from '../../types';
import type { PendingSlot } from '../../screener/screenerTypes';
import type { AcuityReservation } from '../../acuity/acuityTypes';
import { AcuitySlotRenderer } from '../../acuity/AcuitySlotRenderer';
import { RiskBadge } from '../../risk/RiskBadge';
import { ReminderStatusIcon } from '../../risk/ReminderStatusIcon';

// ─── Helpers ───────────────────────────────────────────────────────────────

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ─── Background event expansion ────────────────────────────────────────────
// Background events are NOT added to Kendo's data array (which would cause
// them to participate in the collision layout and shift regular events).
// Instead we expand them into plain time-range objects used by the custom
// slot renderer to color individual cells.

interface BgRange {
  start: Date;
  end: Date;
  color: string;
  opacity: number;
  title?: string;
  /** When set, only applies to the matching resource column */
  resourceId?: string;
}

function expandBackgroundEvents(
  bgEvents: BackgroundEvent[],
  schedulerDate: Date,  // wall-clock reference date (same tz as Kendo's date prop)
  timezone: string,
): BgRange[] {
  const weekStart = startOfWeek(schedulerDate, { weekStartsOn: 1 }); // Monday
  const ranges: BgRange[] = [];

  for (const bg of bgEvents) {
    const push = (start: Date, end: Date) =>
      ranges.push({ start, end, color: bg.color, opacity: bg.opacity ?? 0.25, title: bg.title, resourceId: bg.resourceId });

    if (bg.startUtc && bg.endUtc) {
      push(
        toZonedTime(new Date(bg.startUtc), timezone),
        toZonedTime(new Date(bg.endUtc), timezone),
      );
    } else if (bg.daysOfWeek && bg.startTime && bg.endTime) {
      const [sh, sm] = bg.startTime.split(':').map(Number);
      const [eh, em] = bg.endTime.split(':').map(Number);

      for (let i = 0; i < 7; i++) {
        const day = addDays(weekStart, i);
        if (!bg.daysOfWeek.includes(day.getDay())) continue;

        const start = new Date(day);
        start.setHours(sh, sm, 0, 0);
        const end = new Date(day);
        end.setHours(eh, em, 0, 0);
        push(start, end);
      }
    }
  }

  return ranges;
}

// Returns the working start/end for a specific day, or null if non-working.
// daySchedule entries take priority; falls back to start/end + daysOfWeek.
function resolveWorkingHoursForDay(
  wh: { start: string; end: string; daysOfWeek?: number[]; daySchedule?: { dayOfWeek: number; start: string; end: string }[] },
  dayOfWeek: number,
): { start: string; end: string } | null {
  const dayEntry = wh.daySchedule?.find((d) => d.dayOfWeek === dayOfWeek);
  if (dayEntry) return { start: dayEntry.start, end: dayEntry.end };
  if ((wh.daysOfWeek ?? [1, 2, 3, 4, 5]).includes(dayOfWeek)) return { start: wh.start, end: wh.end };
  return null;
}

// Expands per-resource working hours into non-working overlay ranges. Only
// generates ranges where the resource's hours differ from the visible time
// range (before their start or after their end), so global non-working shading
// is not duplicated here.
function expandPerResourceNonWorkingRanges(
  resources: Resource[],
  schedulerDate: Date,
  timeRange: TimeRange,
): BgRange[] {
  const weekStart = startOfWeek(schedulerDate, { weekStartsOn: 1 }); // Monday
  const ranges: BgRange[] = [];

  const [trsh, trsm] = timeRange.start.split(':').map(Number);
  const [treh, trem] = timeRange.end.split(':').map(Number);
  const trStartMin = trsh * 60 + trsm;
  const trEndMin = treh * 60 + trem;

  for (const resource of resources) {
    if (!resource.workingHours) continue;
    const wh = resource.workingHours;

    const push = (start: Date, end: Date) =>
      ranges.push({ start, end, color: '#000000', opacity: 0.06, resourceId: resource.id });

    for (let i = 0; i < 7; i++) {
      const day = addDays(weekStart, i);
      const dayOfWeek = day.getDay();
      const hours = resolveWorkingHoursForDay(wh, dayOfWeek);

      if (!hours) {
        const s = new Date(day); s.setHours(trsh, trsm, 0, 0);
        const e = new Date(day); e.setHours(treh, trem, 0, 0);
        push(s, e);
      } else {
        const [wsh, wsm] = hours.start.split(':').map(Number);
        const [weh, wem] = hours.end.split(':').map(Number);
        if (wsh * 60 + wsm > trStartMin) {
          const s = new Date(day); s.setHours(trsh, trsm, 0, 0);
          const e = new Date(day); e.setHours(wsh, wsm, 0, 0);
          push(s, e);
        }
        if (weh * 60 + wem < trEndMin) {
          const s = new Date(day); s.setHours(weh, wem, 0, 0);
          const e = new Date(day); e.setHours(treh, trem, 0, 0);
          push(s, e);
        }
      }
    }
  }

  return ranges;
}

// ─── Custom slot renderer ──────────────────────────────────────────────────
// Receives bgRanges via closure. Each slot cell (30-min block) is checked
// for overlap; if it overlaps a background range, its background is colored.
// This never touches Kendo's event layout layer.
//
// Note: SchedulerSlot does NOT spread unknown props — it renders only its own
// known attributes. Kendo already puts data-slot-start / data-slot-end (as ms
// timestamps), data-slot-group, and data-slot on every slot cell automatically.
// We do NOT inject our own data-slot-* here; the drop handler reads Kendo's.

interface PulseRange { start: Date; end: Date; resourceId?: string }

function makeCustomSlot(bgRanges: BgRange[], pulseRanges: PulseRange[]) {
  return function BackgroundSlot(props: SchedulerSlotProps) {
    const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);

    if (!props.isAllDay) {
      const { start, end } = props;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = props.group.resources[0] as any;
      const slotResourceId: string | undefined = res?.[res?.valueField];

      // Backfill-pending pulse takes priority over background coloring.
      const pulsing = pulseRanges.length > 0 && pulseRanges.some(
        (r) => r.start < end && r.end > start && (r.resourceId === undefined || r.resourceId === slotResourceId),
      );
      if (pulsing) {
        return (
          <SchedulerSlot
            {...props}
            style={{
              ...props.style,
              backgroundColor: 'rgba(34,197,94,0.18)',
              animation: 'fse-backfill-pulse 1.5s ease-in-out infinite',
              outline: '2px solid rgba(34,197,94,0.5)',
              outlineOffset: '-2px',
              zIndex: 1,
            }}
          />
        );
      }

      const bg = bgRanges.length > 0 ? bgRanges.find(
        (r) =>
          r.start < end &&
          r.end > start &&
          (r.resourceId === undefined || r.resourceId === slotResourceId),
      ) : undefined;
      if (bg) {
        const handleEnter = bg.title
          ? (e: SchedulerSlotMouseEvent) => setTipPos({ x: e.syntheticEvent.clientX, y: e.syntheticEvent.clientY })
          : undefined;
        const handleLeave = bg.title ? () => setTipPos(null) : undefined;

        return (
          <>
            <SchedulerSlot
              {...props}
              style={{ ...props.style, backgroundColor: hexToRgba(bg.color, bg.opacity) }}
              onMouseEnter={handleEnter}
              onMouseLeave={handleLeave}
            />
            {tipPos && bg.title && createPortal(
              <div style={{
                position: 'fixed',
                left: tipPos.x + 12,
                top: tipPos.y + 12,
                background: '#1f2937',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                lineHeight: '1.4',
                whiteSpace: 'nowrap',
                zIndex: 99999,
                pointerEvents: 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}>
                {bg.title}
              </div>,
              document.body,
            )}
          </>
        );
      }
    }
    return <SchedulerSlot {...props} />;
  };
}

// ─── Custom item renderer ──────────────────────────────────────────────────

const HTML_TAG_RE = /<[a-z][\s\S]*>/i;

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

function ColoredItem(props: SchedulerItemProps) {
  const { config, handleEventClick, handleEventDoubleClick, events, noShowRiskHandle, groupTherapyHandle } = useSchedule();
  const item = props.dataItem as KendoItem;
  const isHoliday = String(item.id).startsWith('__holiday__');
  const isAcuity = String(item.id).startsWith('__acuity__');

  // Delegate acuity events to the dedicated renderer.
  if (isAcuity && item._acuityReservation) {
    return (
      <AcuitySlotRenderer
        {...props}
        reservation={item._acuityReservation as AcuityReservation}
        warningWindowMinutes={config.acuitySlots?.releaseWarningMinutes ?? 30}
      />
    );
  }

  const bg = item.color ?? '#3B82F6';

  const hasHtmlTitle = HTML_TAG_RE.test(item.title);

  // Give Kendo a plain-text dataItem so the native tooltip and the
  // .k-event-template text both show stripped text, not raw markup.
  const displayDataItem = hasHtmlTitle
    ? { ...item, title: stripHtml(item.title) }
    : item;

  const iconNode = config.eventIcon && item._sourceEvent
    ? config.eventIcon(item._sourceEvent)
    : null;

  const severity = item._sourceEvent?.screenerSeverity;
  const SEVERITY_BADGE: Record<string, { bg: string; color: string; label: string }> = {
    low:      { bg: '#16a34a', color: '#fff', label: 'Low' },
    moderate: { bg: '#d97706', color: '#fff', label: 'Mod' },
    high:     { bg: '#dc2626', color: '#fff', label: 'High' },
  };
  const severityBadge = severity && SEVERITY_BADGE[severity]
    ? (
      <span
        title={`Acuity: ${SEVERITY_BADGE[severity].label}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '1px 4px',
          borderRadius: 3,
          fontSize: 9,
          fontWeight: 700,
          lineHeight: 1,
          background: SEVERITY_BADGE[severity].bg,
          color: SEVERITY_BADGE[severity].color,
          letterSpacing: '0.02em',
        }}
      >
        {SEVERITY_BADGE[severity].label}
      </span>
    )
    : null;

  const plainTitle = hasHtmlTitle ? stripHtml(item.title) : undefined;

  // Risk scoring — look up score and reminder state from the hook.
  const riskScore = noShowRiskHandle.enabled && item._sourceEvent
    ? noShowRiskHandle.riskScoresMap.get(item._sourceEvent.id)
    : undefined;
  const reminderState = riskScore && item._sourceEvent
    ? noShowRiskHandle.getReminderState(item._sourceEvent.id)
    : undefined;

  const riskBadgeEl = riskScore && !isHoliday && !isAcuity ? (
    <RiskBadge
      riskLevel={riskScore.riskLevel}
      riskFactors={riskScore.riskFactors}
      suppressReminders={riskScore.suppressReminders ?? false}
    />
  ) : null;

  const reminderStatusEl = reminderState && !isHoliday && !isAcuity ? (
    <ReminderStatusIcon
      appointmentStatus={reminderState.status}
      lastReminderOutcome={reminderState.lastReminderOutcome}
      firedCount={reminderState.firedReminders.length}
      outreachFlagged={reminderState.outreachFlagged}
    />
  ) : null;

  const schedulerItemEl = (
    <SchedulerItem
      {...props}
      dataItem={displayDataItem}
      title={plainTitle ?? props.title}
      className={`${props.className ?? ''} ${hasHtmlTitle ? 'fse-html-title' : ''}`.trim()}
      style={{
        ...props.style,
        background: bg,
        borderColor: bg,
        opacity: item.editable ? 1 : 0.7,
      }}
    >
      {props.children}
      {hasHtmlTitle && (
        <span
          className="fse-event-title-html"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: item.title }}
        />
      )}
      {(iconNode || severityBadge) && (
        <span
          style={{
            position: 'absolute',
            bottom: 2,
            right: 3,
            lineHeight: 1,
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 2,
          }}
        >
          {severityBadge}
          {iconNode}
        </span>
      )}
      {(riskBadgeEl || reminderStatusEl) && (
        <span
          style={{
            position: 'absolute',
            bottom: 2,
            left: 3,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {riskBadgeEl}
          {reminderStatusEl}
        </span>
      )}
    </SchedulerItem>
  );

  // Intercept clicks in the capture phase (fires before Kendo's bubble-phase handlers)
  // when the properties panel is active (single-click shows details) or when the host
  // has opted to handle double-clicks with its own form UI.
  if ((config.propertiesPanel || config.doubleClickForm === 'host' || groupTherapyHandle.enabled) && !isHoliday) {
    return (
      <div
        style={{ display: 'contents' }}
        onClickCapture={(e) => {
          const sourceEvent = item._sourceEvent ?? events.find((ev) => ev.id === String(item.id));
          if (!sourceEvent) return;
          if (e.detail === 2) {
            // Second click of a double-click — notify host and suppress form if requested.
            handleEventDoubleClick(sourceEvent);
          } else if (config.propertiesPanel || groupTherapyHandle.enabled) {
            // Single-click — populate selected event for properties panel or group roster.
            handleEventClick(sourceEvent);
          }
        }}
      >
        {schedulerItemEl}
      </div>
    );
  }

  return schedulerItemEl;
}

// ─── Form wrapper ──────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function SchedulerFormWrapper(props: any) {
  const { suppressNextFormRef, screenerGate, handleSlotIntercepted, handleNewEventDoubleClick, config } = useSchedule();
  const item = (props.dataItem ?? {}) as KendoItem;
  const isNew = !item.id || item.id === '';
  const isHoliday = String(item.id).startsWith('__holiday__');
  const resolvedTz = resolveTimezone(config.timezone, config.observeDaylightSaving);

  // Screener gate: when a new slot is clicked, capture it and let the screener flow handle it.
  // We fire the state update in a useEffect (not during render) to avoid setting state in a
  // different component's render phase — the same pattern as suppressNextFormRef.
  const shouldInterceptForScreener = isNew && (screenerGate?.enabled ?? false);
  const interceptedSlot = React.useRef<PendingSlot | null>(null);
  if (shouldInterceptForScreener && !interceptedSlot.current) {
    interceptedSlot.current = {
      slotId: crypto.randomUUID(),
      resourceId: item.resourceId ?? '',
      start: fromZonedTime(item.start, resolvedTz).toISOString(),
      end: fromZonedTime(item.end, resolvedTz).toISOString(),
      patientId: screenerGate!.patientId ?? '',
    };
  }
  React.useEffect(() => {
    if (interceptedSlot.current) {
      handleSlotIntercepted(interceptedSlot.current);
      interceptedSlot.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Custom double-click form: when a new slot is clicked, forward it to the host and suppress the form.
  const shouldInterceptForHostForm = isNew && config.doubleClickForm === 'host' && !shouldInterceptForScreener;
  const interceptedNewSlot = React.useRef<{ startUtc: string; endUtc: string; resourceId?: string } | null>(null);
  if (shouldInterceptForHostForm && !interceptedNewSlot.current) {
    interceptedNewSlot.current = {
      startUtc: fromZonedTime(item.start, resolvedTz).toISOString(),
      endUtc: fromZonedTime(item.end, resolvedTz).toISOString(),
      resourceId: item.resourceId ?? undefined,
    };
  }
  React.useEffect(() => {
    if (interceptedNewSlot.current) {
      const { startUtc, endUtc, resourceId } = interceptedNewSlot.current;
      handleNewEventDoubleClick(startUtc, endUtc, resourceId);
      interceptedNewSlot.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Holiday events are read-only — close the form immediately if Kendo opens it
  if (isHoliday) {
    props.onClose?.({ target: {}, value: null, syntheticEvent: {} });
    return null;
  }

  // Screener gate active — close the form; the screener dialog will open via useEffect above
  if (shouldInterceptForScreener) {
    props.onClose?.({ target: {}, value: null, syntheticEvent: {} });
    return null;
  }

  // Host double-click form active for new slots — close the form; host UI opens via useEffect above
  if (shouldInterceptForHostForm) {
    props.onClose?.({ target: {}, value: null, syntheticEvent: {} });
    return null;
  }

  // Host returned true from onEventDoubleClick — suppress the default edit form
  if (!isNew && suppressNextFormRef.current) {
    suppressNextFormRef.current = false;
    props.onClose?.({ target: {}, value: null, syntheticEvent: {} });
    return null;
  }

  return (
    <CustomEditForm
      dataItem={item}
      isNew={isNew}
      onClose={() => props.onClose?.({ target: {}, value: null, syntheticEvent: {} })}
      onSubmit={(updatedItem) =>
        props.onSubmit({ target: {}, value: updatedItem, syntheticEvent: {} })
      }
    />
  );
}

// ─── Custom date-column header ─────────────────────────────────────────────
// Kendo exposes a dateHeaderCell prop on each view that accepts a component
// matching DateHeaderCellProps.  We create one per view via a factory so
// format options are closed over and the component reference is stable.

function makeDateHeaderCell(formatOptions: Intl.DateTimeFormatOptions, timezone: string) {
  return function CustomDateHeaderCell({
    date,
    start: _start,
    end: _end,
    format: _fmt,
    as: _as,
    className,
    ...rest
  }: DateHeaderCellProps) {
    const formatted = new Intl.DateTimeFormat('en-US', { timeZone: timezone, ...formatOptions }).format(date);
    const cls = ['k-scheduler-cell', 'k-heading-cell', className].filter(Boolean).join(' ');
    return (
      <div className={cls} {...rest}>
        <span className="k-link k-nav-day">{formatted}</span>
      </div>
    );
  };
}

// ─── Time-column override ──────────────────────────────────────────────────
// Kendo does not expose a public API for customising the left time-gutter, so
// we use a MutationObserver to intercept and replace its time-label cells.
// The observer disconnects before applying changes to prevent re-entrant loops.

interface SlotLabel {
  primary: string;
  secondary: { abbr: string; time: string }[];
}

function useTimeColumnOverride(
  scopeId: string,
  timeRange: TimeRange,
  slotDuration: number,
  timezone: string,
  timeFormat: TimeFormat,
  secondaryTimezones: SecondaryTimezone[],
  currentDate: Date,
  showRightTimeColumn: boolean,
) {
  // Hold the latest apply() so the long-lived observer always calls the
  // current version without needing to be recreated on every input change.
  const applyRef = useRef<() => void>(() => {});

  // Recompute labels and update applyRef whenever inputs change, then apply.
  // useLayoutEffect (not useEffect) so the DOM injection runs synchronously after
  // React's commit — before Kendo's child useEffect(b) reads slot widths to
  // position events. This ensures events are sized against the correct layout.
  useLayoutEffect(() => {
    const startMin = timeToMinutes(timeRange.start);
    const endMin   = timeToMinutes(timeRange.end);

    // Wall-clock reference for DST-aware secondary-TZ conversions.
    const refWall = toZonedTime(currentDate, timezone);

    const labels: SlotLabel[] = [];
    for (let m = startMin; m < endMin; m += slotDuration) {
      const h   = Math.floor(m / 60);
      const min = m % 60;

      // Build a wall-clock Date at this slot time in the primary timezone.
      const wallSlot = new Date(refWall);
      wallSlot.setHours(h, min, 0, 0);
      const utcMoment = fromZonedTime(wallSlot, timezone);

      labels.push({
        primary: formatTimeLabel(h, min, timeFormat),
        secondary: secondaryTimezones.map((st) => {
          const secWall = toZonedTime(utcMoment, st.timezone);
          return {
            abbr: st.label ?? getTimezoneAbbr(st.timezone, utcMoment),
            time: formatTimeLabel(secWall.getHours(), secWall.getMinutes(), timeFormat),
          };
        }),
      });
    }

    const hasSecondary = secondaryTimezones.length > 0;

    function apply() {
      const container = document.getElementById(scopeId);
      if (!container) return;

      // Kendo v15 renders the time gutter as divs, not a <table>.
      // Each time-slot row has a leading div with classes:
      //   k-scheduler-cell  k-heading-cell  k-side-cell  [k-major-cell]
      // The all-day cell adds k-scheduler-times-all-day – exclude it.
      // These cells live inside .k-scheduler-body (excludes the head area).
      const timeCells = Array.from(
        container.querySelectorAll<HTMLElement>(
          '.k-scheduler-body .k-heading-cell.k-side-cell:not(.k-scheduler-times-all-day)',
        ),
      );

      timeCells.forEach((cell, i) => {
        if (i >= labels.length) return;
        const { primary, secondary } = labels[i];

        // Left column: skip update if content is already correct.
        const key = `${timeFormat}|${primary}|${secondary.map((s) => `${s.abbr}:${s.time}`).join(',')}`;
        if (cell.dataset.fseKey !== key) {
          cell.dataset.fseKey = key;

          if (!hasSecondary) {
            // Kendo puts the time text directly inside the div (no child span).
            cell.textContent = primary;
          } else {
            const secHtml = secondary
              .map(
                (s) =>
                  `<div class="fse-tz-col">` +
                  `<div class="fse-tz-name">${s.abbr}</div>` +
                  `<div class="fse-tz-val">${s.time}</div>` +
                  `</div>`,
              )
              .join('');
            cell.innerHTML =
              `<div class="fse-tz-wrapper">` +
              secHtml +
              `<div class="fse-tz-col fse-tz-primary"><div class="fse-tz-val">${primary}</div></div>` +
              `</div>`;
          }
        }

        // Right time column: always evaluated independently of left column key.
        const row = cell.parentElement;
        if (row) {
          if (showRightTimeColumn) {
            let rightCell = row.querySelector<HTMLElement>('.fse-right-time-col');
            if (!rightCell) {
              rightCell = document.createElement('div');
              rightCell.className = 'fse-right-time-col';
            }
            // Always append — moves the element to the end if Kendo inserted
            // resource columns after it (e.g. when toggling Show Resources).
            row.appendChild(rightCell);
            const rightKey = `rtc|${timeFormat}|${primary}`;
            if (rightCell.dataset.fseKey !== rightKey) {
              rightCell.dataset.fseKey = rightKey;
              rightCell.textContent = primary;
            }
          } else {
            row.querySelector('.fse-right-time-col')?.remove();
          }
        }
      });

      // When secondary timezones are removed, Kendo's useCellSync still holds
      // the stale inline minWidth from the wider multi-TZ state. Its ResizeObserver
      // doesn't re-fire reliably in this scenario, so we replicate its logic:
      // clear the inline constraint, then measure each body time cell's
      // content-box width (getComputedStyle forces a synchronous reflow),
      // then pin all side cells to the new max — matching what useCellSync does.
      if (!hasSecondary) {
        const allSideCells = Array.from(container.querySelectorAll<HTMLElement>('.k-side-cell'));
        allSideCells.forEach((el) => { el.style.minWidth = ''; });
        // Measure only body time cells (same set we just updated content for)
        const maxW = Math.max(0, ...timeCells.map((el) => parseFloat(getComputedStyle(el).width) || 0));
        if (maxW > 0) {
          allSideCells.forEach((el) => { el.style.minWidth = `${maxW}px`; });
        }
      }

      // Head spacer so the right column width is accounted for in the sticky header.
      // Only target outer rows (those with a direct .k-side-cell child) — inner rows
      // inside .k-group-cell must not receive a spurious spacer.
      if (showRightTimeColumn) {
        container.querySelectorAll<HTMLElement>('.k-scheduler-head .k-scheduler-row').forEach((row) => {
          if (row.querySelector(':scope > .k-side-cell')) {
            let spacer = row.querySelector<HTMLElement>(':scope > .fse-right-time-head');
            if (!spacer) {
              spacer = document.createElement('div');
              spacer.className = 'fse-right-time-head';
            }
            // Always append to stay last even after Kendo inserts resource columns.
            row.appendChild(spacer);
          }
        });
      } else {
        container.querySelectorAll('.fse-right-time-head').forEach((el) => el.remove());
      }
    }

    applyRef.current = apply;
    apply();
  // secondaryTimezones is an array – JSON.stringify gives stable comparison.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, timeRange, slotDuration, timezone, timeFormat,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(secondaryTimezones), currentDate, showRightTimeColumn]);

  // Long-lived MutationObserver reacts to Kendo re-renders.
  // Disconnect before applying to prevent our own DOM changes from re-firing.
  useEffect(() => {
    const container = document.getElementById(scopeId);
    if (!container) return;

    const observer = new MutationObserver(() => {
      observer.disconnect();
      applyRef.current();
      observer.observe(container, { childList: true, subtree: true });
    });

    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [scopeId]);
}

// ─── Strip HTML from native title tooltips ────────────────────────────────
// Kendo sets the HTML `title` attribute on event elements from dataItem.title.
// When titles contain markup the browser tooltip shows raw HTML, so we scan
// every element inside the scheduler and strip tags from any title attribute
// that contains them.  Uses the same disconnect-before-mutate pattern as
// useTimeColumnOverride to avoid re-entrant observer loops.

function useStripHtmlTitles(scopeId: string) {
  useEffect(() => {
    const container = document.getElementById(scopeId);
    if (!container) return;

    function apply() {
      container!.querySelectorAll<HTMLElement>('[title]').forEach((el) => {
        const t = el.getAttribute('title');
        if (t && HTML_TAG_RE.test(t)) {
          el.setAttribute('title', stripHtml(t));
        }
      });
    }

    const observer = new MutationObserver(() => {
      observer.disconnect();
      apply();
      observer.observe(container, { childList: true, subtree: true });
    });

    apply();
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [scopeId]);
}

// ─── Main view ─────────────────────────────────────────────────────────────

export function KendoSchedulerView() {
  const {
    config,
    events,
    currentDate,
    setCurrentDate,
    setView,
    callbacks,
    emit,
    handleDeleteEvent,
    handleSaveEvent,
    handleCreateEvent,
    resourcePage,
    setResourcePage,
    holidayEvents,
    queuedEvents,
    queueDragRef,
    handleScheduleQueuedEvent,
    pendingNewEventRequest,
    setPendingNewEventRequest,
    acuityReservations,
    privilegedView,
    waitlistHandle,
  } = useSchedule();

  // AI-initiated create: open the form when the AI returns a createEvent request
  const [aiCreateItem, setAiCreateItem] = React.useState<KendoItem | null>(null);

  // Resource jump picker
  const [showResourcePicker, setShowResourcePicker] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState<DOMRect | null>(null);
  const resourcePickerBtnRef = useRef<HTMLButtonElement>(null);
  const resourcePickerPopupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showResourcePicker) return;
    const handler = (e: MouseEvent) => {
      if (
        resourcePickerBtnRef.current?.contains(e.target as Node) ||
        resourcePickerPopupRef.current?.contains(e.target as Node)
      ) return;
      setShowResourcePicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showResourcePicker]);

  React.useEffect(() => {
    if (!pendingNewEventRequest) return;
    const { date, startTime, resourceId, durationMinutes } = pendingNewEventRequest;
    const [y, m, d] = date.split('-').map(Number);
    const [h, min] = startTime.split(':').map(Number);
    const wallStart = new Date(y, m - 1, d, h, min, 0, 0);
    const duration = durationMinutes ?? (config.slotDuration ?? 30);
    setAiCreateItem({
      ...blankKendoItem(wallStart, duration),
      resourceId,
      ...(pendingNewEventRequest.title !== undefined && { title: pendingNewEventRequest.title }),
      ...(pendingNewEventRequest.customAttributes?.length && { customAttributes: pendingNewEventRequest.customAttributes }),
    });
    setPendingNewEventRequest(null);
  }, [pendingNewEventRequest, config.slotDuration, setPendingNewEventRequest]);

  const {
    view,
    timezone,
    observeDaylightSaving,
    slotDuration,
    timeRange,
    workingHours,
    backgroundEvents = [],
    allowEventCreation = true,
    resources,
    resourceGrouping,
    resourcesPerPage: resourcesPerPageConfig,
    showFullDay = false,
    showKendoToolbar = false,
    showAllDaySection = true,
    allDayLabel,
    timeFormat = '12h',
    secondaryTimezones = [],
    columnHeaderFormats = {},
    showRightTimeColumn = false,
  } = config;

  const resolvedTimezone = resolveTimezone(timezone, observeDaylightSaving);

  const scopeId = useId().replace(/:/g, '_');

  // Resolve the page size for the active view. Accepts a flat number or a
  // per-view map; falls back to 2 when neither covers the current view.
  const resourcesPerPage =
    typeof resourcesPerPageConfig === 'number'
      ? resourcesPerPageConfig
      : (resourcesPerPageConfig?.[view] ?? 2);

  // resourcePage / setResourcePage come from ScheduleContext so the ResourcePanel
  // and this view share the same pagination state.

  const paginationActive =
    resourceGrouping !== 'byResource' &&
    (resources?.length ?? 0) > resourcesPerPage;

  const totalResourcePages = paginationActive
    ? Math.ceil((resources?.length ?? 0) / resourcesPerPage)
    : 1;

  // Slice of resources visible on the current page (all resources when
  // pagination is inactive, e.g. byResource mode or small resource lists).
  const visibleResources = paginationActive
    ? resources?.slice(resourcePage * resourcesPerPage, (resourcePage + 1) * resourcesPerPage)
    : resources;

  // When showing the full day, generate time labels for all 24 hours.
  // '24:00' is valid for the label math (timeToMinutes returns 1440) even
  // though it's invalid as a Kendo view startTime/endTime prop.
  const effectiveTimeRange = showFullDay ? { start: '00:00', end: '24:00' } : timeRange;

  useTimeColumnOverride(
    scopeId, effectiveTimeRange, slotDuration, resolvedTimezone,
    timeFormat, secondaryTimezones, currentDate, showRightTimeColumn,
  );
  useStripHtmlTitles(scopeId);

  // TV mode: keep the current time visible and close to the top of the view.
  // The scrollable container in Kendo v15 day view is .k-scheduler-layout-flex.
  // We position by ratio: (currentMinutes - startMinutes) / totalMinutes × scrollHeight,
  // then subtract 15% of the visible height so upcoming events fill the screen.
  // Kendo resets scrollTop during its own post-render initialization, so we fire
  // at several delays (150 ms, 400 ms, 900 ms) to win the race.
  useEffect(() => {
    if (!config.tvMode?.enabled || view !== 'day') return;

    function scrollToNow() {
      const container = document.getElementById(scopeId);
      if (!container) return;
      const scrollEl = container.querySelector<HTMLElement>('.k-scheduler-layout-flex');
      if (!scrollEl || scrollEl.scrollHeight <= scrollEl.clientHeight) return;

      const nowZoned = toZonedTime(new Date(), resolvedTimezone);
      const nowMin = nowZoned.getHours() * 60 + nowZoned.getMinutes();
      const startMin = timeToMinutes(effectiveTimeRange.start);
      // '24:00' is valid for timeToMinutes (returns 1440); Kendo uses '00:00' as
      // the end marker for full-day but our range label uses '24:00'.
      const endMin = timeToMinutes(effectiveTimeRange.end === '00:00' ? '24:00' : effectiveTimeRange.end);
      if (endMin <= startMin) return;

      const ratio = Math.max(0, Math.min(1, (nowMin - startMin) / (endMin - startMin)));
      const targetScrollTop = ratio * scrollEl.scrollHeight - scrollEl.clientHeight * 0.15;
      scrollEl.scrollTop = Math.max(0, targetScrollTop);
    }

    const timers = [150, 400, 900].map((ms) => setTimeout(scrollToNow, ms));
    const interval = setInterval(scrollToNow, 60_000);
    return () => { timers.forEach(clearTimeout); clearInterval(interval); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.tvMode?.enabled, view, scopeId, resolvedTimezone, effectiveTimeRange.start, effectiveTimeRange.end]);

  // Wall-clock reference date for Kendo's date prop
  const schedulerDate = toZonedTime(currentDate, resolvedTimezone);

  // Regular events — excludes queued events and background events.
  // Queued events are never rendered by Kendo; they live in the scheduling queue panel.
  // When pagination is active, restrict to the current page's resources so
  // Kendo's layout engine only processes the visible slice instead of all events.
  // When hideUnassignedEvents is true, events with no resourceId are also excluded
  // whenever resources are configured.
  const eventItems = useMemo(() => {
    const hideUnassigned = config.hideUnassignedEvents && !!config.resources?.length;
    let filtered = events.filter((e) => !e.queued);
    if (visibleResources?.length) {
      const visibleIds = new Set(visibleResources.map((r) => r.id));
      filtered = filtered.filter((e) => {
        const ids = e.resourceIds?.length ? e.resourceIds : (e.resourceId ? [e.resourceId] : []);
        const hasResource = ids.length > 0;
        const hasVisible = ids.some((id) => visibleIds.has(id));
        return hideUnassigned ? (hasResource && hasVisible) : (!hasResource || hasVisible);
      });
    } else if (hideUnassigned) {
      filtered = filtered.filter((e) => !!(e.resourceIds?.length || e.resourceId));
    }
    return filtered.map((e) => toKendoItem(e, resolvedTimezone));
  }, [events, resolvedTimezone, paginationActive, visibleResources, config.hideUnassignedEvents, config.resources]);

  // Holiday events merged in as read-only all-day items
  // Convert AcuityReservation[] to synthetic KendoItems.
  // Slots where visibleToGeneral is false are hidden unless the host passes privilegedView.
  const acuityItems = useMemo((): KendoItem[] => {
    if (!acuityReservations.length) return [];
    return acuityReservations
      .filter((r) => r.visibleToGeneral || privilegedView)
      .map((r): KendoItem => ({
        id: `__acuity__${r.id}`,
        title: r.label ?? r.acuityLevel,
        start: toZonedTime(new Date(r.start), resolvedTimezone),
        end: toZonedTime(new Date(r.end), resolvedTimezone),
        startTimezone: null,
        endTimezone: null,
        isAllDay: false,
        recurrenceRule: null,
        recurrenceId: null,
        recurrenceExceptions: null,
        resourceId: r.resourceId,
        resourceIds: r.resourceId ? [r.resourceId] : [],
        color: '#3b82f6',
        description: '',
        customAttributes: [],
        editable: false,
        deletable: false,
        _acuityReservation: r,
      }));
  }, [acuityReservations, privilegedView, resolvedTimezone]);

  const allEventItems = useMemo(
    () => [
      ...eventItems,
      ...holidayEvents.map((e) => toKendoItem(e, resolvedTimezone)),
      ...acuityItems,
    ],
    [eventItems, holidayEvents, resolvedTimezone, acuityItems],
  );

  // Build Kendo resource config from the VISIBLE resource slice so Kendo only
  // renders columns for the current page.
  const kendoResources = useMemo((): SchedulerResource[] | undefined => {
    if (!visibleResources?.length) return undefined;
    return [{
      name: 'People',
      field: 'resourceIds',
      data: visibleResources.map((r) => ({ id: r.id, text: r.name, color: r.color ?? '#3B82F6' })),
      valueField: 'id',
      textField: 'text',
      colorField: 'color',
      multiple: true,
    }];
  }, [visibleResources]);

  // 'byDate'     → horizontal (resources as sub-columns within each day)
  // 'byResource' → vertical   (resources as top-level groups, days within)
  const kendoGroup = useMemo((): SchedulerGroup | undefined => {
    if (!visibleResources?.length) return undefined;
    return {
      resources: ['People'],
      orientation: resourceGrouping === 'byResource' ? 'vertical' : 'horizontal',
    };
  }, [visibleResources, resourceGrouping]);

  // Expand background ranges for the current week.
  // Per-resource non-working ranges are listed after background events so that
  // background events take visual priority when both apply to the same slot.
  const bgRanges = useMemo(
    () => [
      ...expandBackgroundEvents(backgroundEvents, schedulerDate, resolvedTimezone),
      ...(visibleResources?.length
        ? expandPerResourceNonWorkingRanges(visibleResources, schedulerDate, timeRange)
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [backgroundEvents, visibleResources, schedulerDate.toDateString(), resolvedTimezone, timeRange],
  );

  // Pulse ranges: one entry per backfill-pending slot (green animation).
  const pulseRanges = useMemo((): PulseRange[] => {
    const { backfillPendingMap } = waitlistHandle;
    const result: PulseRange[] = [];
    backfillPendingMap.forEach((state) => {
      result.push({ start: new Date(state.start), end: new Date(state.end), resourceId: state.resourceId });
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitlistHandle.backfillPendingMap]);

  // Stable slot component — only recreated when bgRanges or pulseRanges change.
  const CustomSlot = useMemo(() => makeCustomSlot(bgRanges, pulseRanges), [bgRanges, pulseRanges]);

  // ── Data change handler ──────────────────────────────────────────────────
  const handleDataChange = (ev: SchedulerDataChangeEvent) => {
    if (ev.created?.length) {
      ev.created.forEach((created) => {
        const createdItem = created as KendoItem;
        // Acuity synthetic events are read-only — ignore spurious create events.
        if (String(createdItem.id).startsWith('__acuity__')) return;
        const ourEvent = fromKendoItem(createdItem, resolvedTimezone);
        const { id: _id, ...rest } = ourEvent;
        handleCreateEvent(rest);
        callbacks.onSlotClick?.({ dateUtc: ourEvent.startUtc });
        emit({ type: 'SLOT_CLICKED', payload: { dateUtc: ourEvent.startUtc } });
      });
    }

    if (ev.updated?.length) {
      ev.updated.forEach((updated) => {
        const updatedItem = updated as KendoItem;
        // Skip holiday and acuity events — they are read-only and must never fire callbacks
        if (String(updatedItem.id).startsWith('__holiday__')) return;
        if (String(updatedItem.id).startsWith('__acuity__')) return;
        const original = eventItems.find((k) => k.id === String(updatedItem.id));
        const ourEvent = fromKendoItem(updatedItem, resolvedTimezone);

        if (original) {
          const kind = detectChangeKind(original, updatedItem);
          if (kind === 'moved') {
            const payload: EventMovedPayload = {
              eventId: ourEvent.id,
              originalStartUtc: fromZonedTime(original.start, resolvedTimezone).toISOString(),
              originalEndUtc: fromZonedTime(original.end, resolvedTimezone).toISOString(),
              newStartUtc: ourEvent.startUtc,
              newEndUtc: ourEvent.endUtc,
              newResourceId: ourEvent.resourceId,
            };
            callbacks.onEventMoved?.(payload);
            emit({ type: 'EVENT_MOVED', payload });
          } else if (kind === 'resized-start' || kind === 'resized-end') {
            const payload: EventResizedPayload = {
              eventId: ourEvent.id,
              originalStartUtc: fromZonedTime(original.start, resolvedTimezone).toISOString(),
              originalEndUtc: fromZonedTime(original.end, resolvedTimezone).toISOString(),
              newStartUtc: ourEvent.startUtc,
              newEndUtc: ourEvent.endUtc,
            };
            callbacks.onEventResized?.(payload);
            emit({ type: 'EVENT_RESIZED', payload });
          }
        }

        handleSaveEvent(ourEvent);
      });
    }

    if (ev.deleted?.length) {
      ev.deleted.forEach((d) => {
        const id = String((d as KendoItem).id);
        if (id.startsWith('__holiday__')) return;
        if (id.startsWith('__acuity__')) return;
        handleDeleteEvent(id);
      });
    }
  };

  const handleDateChange = (ev: SchedulerDateChangeEvent) => {
    setCurrentDate(fromZonedTime(ev.value, resolvedTimezone));
  };

  const handleViewChange = (ev: SchedulerViewChangeEvent) => {
    if (ev.value === 'day') setView('day');
    else if (ev.value === 'work-week') setView('weekdays');
    else if (ev.value === 'week') setView('fullWeek');
    else if (ev.value === 'month') setView('month');
  };

  const kendoView =
    view === 'day' ? 'day' :
    view === 'weekdays' ? 'work-week' :
    view === 'month' ? 'month' :
    'week';
  const workStart = workingHours?.start ?? timeRange.start;
  const workEnd = workingHours?.end ?? timeRange.end;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const EmptySlot = () => null as any;
  // Always hide the Kendo footer toggle — showFullDay overrides the time range
  // instead of toggling the footer button.
  const footerProp = EmptySlot;

  // loadMessages uses Object.defineProperty internally and throws if you try to
  // redefine the same key on the same language string. Work around this by
  // incorporating the label value into the language key — each unique label gets
  // its own registry entry, so nothing is ever redefined.
  const langKey = allDayLabel ? `${scopeId}__${allDayLabel}` : scopeId;
  useMemo(() => {
    if (allDayLabel) {
      loadMessages({ 'scheduler.allDay': allDayLabel }, langKey);
    }
  }, [allDayLabel, langKey]);

  // Custom date-column header cells — one per view, recreated only when the
  // format or timezone changes.  undefined → Kendo uses its own default.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dayHeaderCell = useMemo(() => columnHeaderFormats.day ? makeDateHeaderCell(columnHeaderFormats.day, resolvedTimezone) : undefined, [JSON.stringify(columnHeaderFormats.day), resolvedTimezone]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekdaysHeaderCell = useMemo(() => columnHeaderFormats.weekdays ? makeDateHeaderCell(columnHeaderFormats.weekdays, resolvedTimezone) : undefined, [JSON.stringify(columnHeaderFormats.weekdays), resolvedTimezone]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fullWeekHeaderCell = useMemo(() => columnHeaderFormats.fullWeek ? makeDateHeaderCell(columnHeaderFormats.fullWeek, resolvedTimezone) : undefined, [JSON.stringify(columnHeaderFormats.fullWeek), resolvedTimezone]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const monthHeaderCell = useMemo(() => columnHeaderFormats.month ? makeDateHeaderCell(columnHeaderFormats.month, resolvedTimezone) : undefined, [JSON.stringify(columnHeaderFormats.month), resolvedTimezone]);

  // Width of each secondary timezone column and the primary column (px).
  // Values are generous enough to fit "12:30 PM" at each column's font-size
  // so overflow: hidden never visually clips time labels.
  const TZ_COL_W  = 68;
  const PRI_COL_W = 76;
  const timesWidth = PRI_COL_W + secondaryTimezones.length * TZ_COL_W;

  // Build all scoped CSS overrides in one block to avoid multiple <style> tags.
  const scopedCss = [
    !showKendoToolbar
      ? `#${scopeId} .k-scheduler-toolbar { display: none !important; }`
      : '',
    !showAllDaySection
      ? `#${scopeId} .k-scheduler-head .k-scheduler-row:has(.k-scheduler-times-all-day) { display: none !important; }`
      : '',
    !showAllDaySection
      ? `#${scopeId} .k-scheduler-head .k-event { display: none !important; }`
      : '',
    // Widen the time column.
    // Body time cells: .k-heading-cell.k-side-cell
    // Head spacer cell (day-name row): .k-side-cell inside .k-scheduler-head — it
    //   does NOT carry k-heading-cell, so it must be targeted separately.
    secondaryTimezones.length > 0
      ? `#${scopeId} .k-heading-cell.k-side-cell,
         #${scopeId} .k-scheduler-head .k-side-cell
         { width: ${timesWidth}px !important; min-width: ${timesWidth}px !important; flex-shrink: 0; }`
      : '',
    // Layout for multi-timezone cells.
    `#${scopeId} .fse-tz-wrapper { display: flex; align-items: flex-start; justify-content: flex-end; width: 100%; height: 100%; }`,
    // Fixed widths (not min-width) prevent individual rows from growing wider
    // when longer time strings like "10:00 AM" or "12:30 PM" are rendered.
    `#${scopeId} .fse-tz-col { display: flex; flex-direction: column; align-items: center; justify-content: flex-start; width: ${TZ_COL_W}px; flex-shrink: 0; overflow: hidden; padding: 2px 4px 0; border-right: 1px solid #e2e8f0; box-sizing: border-box; }`,
    `#${scopeId} .fse-tz-col.fse-tz-primary { border-right: none; width: ${PRI_COL_W}px; overflow: hidden; align-items: flex-end; padding-right: 6px; }`,
    `#${scopeId} .fse-tz-name { font-size: 9px; color: #94a3b8; line-height: 1; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; }`,
    `#${scopeId} .fse-tz-val { font-size: 11px; color: #64748b; line-height: 1.2; white-space: nowrap; }`,
    `#${scopeId} .fse-tz-col.fse-tz-primary .fse-tz-val { color: #1e293b; font-weight: 500; font-size: 12px; }`,
    // Right-side time column mirror — font tracks the left column state:
    // single-TZ → 14px/700/Kendo surface color; multi-TZ → 12px/500/#1e293b
    // (matching .fse-tz-primary .fse-tz-val). Width/border/layout are constant.
    showRightTimeColumn
      ? `#${scopeId} .fse-right-time-col { width: 80px; min-width: 80px; flex-shrink: 0; display: flex; align-items: flex-start; justify-content: flex-end; padding: 8px; font-size: ${secondaryTimezones.length > 0 ? '12px' : '14px'}; font-weight: ${secondaryTimezones.length > 0 ? '500' : '700'}; color: ${secondaryTimezones.length > 0 ? '#1e293b' : 'var(--kendo-color-on-app-surface, oklch(0.36 0 0))'}; background-color: transparent; border-left: 1px solid var(--kendo-color-border, oklch(0 0 0 / 0.08)); border-bottom: 1px solid var(--kendo-color-border, oklch(0 0 0 / 0.08)); box-sizing: border-box; white-space: nowrap; overflow: hidden; }`
      : '',
    showRightTimeColumn
      ? `#${scopeId} .fse-right-time-head { width: 80px; min-width: 80px; flex-shrink: 0; border-left: 1px solid var(--kendo-color-border, oklch(0 0 0 / 0.08)); box-sizing: border-box; }`
      : '',
    // HTML event title support: hide Kendo's plain-text template content,
    // restore the time sub-label, and position our HTML overlay on top.
    `#${scopeId} .fse-html-title .k-event-template { color: transparent !important; }`,
    `#${scopeId} .fse-html-title .k-event-time { color: rgba(255,255,255,0.85) !important; }`,
    `#${scopeId} .fse-event-title-html { position: absolute; top: 0; left: 0; right: 0; padding: 2px 4px; overflow: hidden; color: inherit; font-size: inherit; line-height: 1.3; pointer-events: none; }`,
    // While a wait list item is being dragged over the scheduler, disable
    // pointer events on event blocks so drops always land on slot cells
    // and the data-slot-* attributes can be read from the drop target.
    `#${scopeId}.fse-waitlist-dragging .k-event { pointer-events: none !important; }`,
    // Highlight the slot cell currently under the drag cursor.
    `#${scopeId} .fse-drag-hover { background-color: rgba(59,130,246,0.18) !important; box-shadow: inset 0 0 0 2px rgba(59,130,246,0.7) !important; position: relative; z-index: 1; }`,
    // Backfill-pending slot pulse animation (green glow).
    `@keyframes fse-backfill-pulse { 0%,100% { background-color: rgba(34,197,94,0.12); } 50% { background-color: rgba(34,197,94,0.32); } }`,
    `@keyframes fse-notify-slide-in { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`,
    `@media (prefers-reduced-motion: reduce) { @keyframes fse-backfill-pulse { from,to { background-color: rgba(34,197,94,0.22); } } }`,
  ].filter(Boolean).join('\n');

  // ── Wait list drag-and-drop ──────────────────────────────────────────────
  // capture:true fires our listeners BEFORE any Kendo child listeners so
  // e.preventDefault() is guaranteed regardless of stopPropagation in children.
  //
  // Kendo's SchedulerSlot already adds these attributes to each slot div:
  //   data-slot="true"          — marks it as a slot cell
  //   data-slot-allday          — "true" | "false"
  //   data-slot-start           — Unix ms timestamp (NOT an ISO string)
  //   data-slot-end             — Unix ms timestamp
  //   data-slot-group           — group index (maps to resource via % numResources)
  // We read these natively in the drop handler and convert ms → ISO.
  const schedulerDropRef = useRef<HTMLDivElement>(null);

  // Always reflects the current visible resources slice without causing the
  // effect to re-run on every pagination change.
  const visibleResourcesRef = useRef(visibleResources);
  useEffect(() => { visibleResourcesRef.current = visibleResources; }, [visibleResources]);

  useEffect(() => {
    const dropEl = schedulerDropRef.current;
    if (!dropEl) return;

    // Ghost preview element — positioned over the hovered slot.
    const ghost = document.createElement('div');
    ghost.style.cssText = [
      'position:fixed',
      'pointer-events:none',
      'z-index:9999',
      'background:rgba(59,130,246,0.82)',
      'border:2px solid #2563eb',
      'border-radius:4px',
      'padding:3px 6px',
      'color:#fff',
      'font-size:12px',
      'font-weight:600',
      'line-height:1.4',
      'overflow:hidden',
      'white-space:nowrap',
      'text-overflow:ellipsis',
      'display:none',
      'box-shadow:0 2px 8px rgba(0,0,0,0.25)',
    ].join(';');
    document.body.appendChild(ghost);

    let currentSlotEl: Element | null = null;

    // Walk up from `from` to find the nearest Kendo time-slot div.
    // Kendo marks every non-all-day slot with data-slot="true" and
    // data-slot-allday="false". We stop at dropEl so we never escape the grid.
    function findSlotEl(from: Element | null): Element | null {
      let el = from;
      while (el && el !== dropEl) {
        if (
          el.getAttribute('data-slot') === 'true' &&
          el.getAttribute('data-slot-allday') === 'false'
        ) return el;
        el = el.parentElement;
      }
      return null;
    }

    // Read a Kendo ms-timestamp attribute and return an ISO string, or null.
    function msAttrToIso(el: Element, attr: string): string | null {
      const raw = el.getAttribute(attr);
      if (!raw) return null;
      const ms = parseInt(raw, 10);
      return isNaN(ms) ? null : new Date(ms).toISOString();
    }

    function clearSlot() {
      currentSlotEl?.classList.remove('fse-drag-hover');
      currentSlotEl = null;
      ghost.style.display = 'none';
    }

    function updatePreview(e: DragEvent) {
      const slotEl = findSlotEl(e.target as Element);
      if (slotEl === currentSlotEl) return;

      currentSlotEl?.classList.remove('fse-drag-hover');
      currentSlotEl = slotEl;

      if (!slotEl) {
        ghost.style.display = 'none';
        return;
      }

      slotEl.classList.add('fse-drag-hover');

      const rect = slotEl.getBoundingClientRect();
      const eventId = queueDragRef.current;
      const sourceEvent = eventId ? queuedEvents.find((ev) => ev.id === eventId) : null;
      const slotDurMs = slotDuration * 60000;
      const eventDurMs =
        sourceEvent?.startUtc && sourceEvent?.endUtc
          ? new Date(sourceEvent.endUtc).getTime() - new Date(sourceEvent.startUtc).getTime()
          : slotDurMs;
      const numSlots = Math.max(1, Math.round(eventDurMs / slotDurMs));

      ghost.style.left = `${rect.left + 2}px`;
      ghost.style.top = `${rect.top}px`;
      ghost.style.width = `${rect.width - 8}px`;
      ghost.style.height = `${rect.height * numSlots}px`;
      ghost.style.display = 'block';
      ghost.textContent = sourceEvent?.title ?? 'Event';
    }

    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('text/plain')) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      updatePreview(e);
    };

    const onDragEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('text/plain')) return;
      e.preventDefault();
      document.getElementById(scopeId)?.classList.add('fse-waitlist-dragging');
    };

    const onDragLeave = (e: DragEvent) => {
      if (!dropEl.contains(e.relatedTarget as Node)) {
        document.getElementById(scopeId)?.classList.remove('fse-waitlist-dragging');
        clearSlot();
      }
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      document.getElementById(scopeId)?.classList.remove('fse-waitlist-dragging');
      clearSlot();

      const eventId = e.dataTransfer?.getData('text/plain') || queueDragRef.current;
      if (!eventId) return;
      queueDragRef.current = null;

      // Walk up from drop target to find the Kendo slot element.
      const slotEl = findSlotEl(e.target as Element);
      if (!slotEl) return;

      // Kendo stores timestamps as Unix ms; convert to ISO for our event model.
      const slotStart = msAttrToIso(slotEl, 'data-slot-start');
      const slotEnd = msAttrToIso(slotEl, 'data-slot-end');
      if (!slotStart) return;

      // Map data-slot-group index to resource ID.
      // For horizontal (byDate) grouping: groupIndex % numResources → resource.
      const groupAttr = slotEl.getAttribute('data-slot-group');
      const groupIdx = groupAttr !== null ? parseInt(groupAttr, 10) : -1;
      const vr = visibleResourcesRef.current;
      const resourceId =
        vr?.length && groupIdx >= 0 ? vr[groupIdx % vr.length]?.id : undefined;

      // Preserve the event's original duration; default to one slot period.
      const source = queuedEvents.find((ev) => ev.id === eventId);
      let endUtc: string;
      if (source?.startUtc && source?.endUtc) {
        const dur = new Date(source.endUtc).getTime() - new Date(source.startUtc).getTime();
        endUtc = new Date(new Date(slotStart).getTime() + dur).toISOString();
      } else if (slotEnd) {
        endUtc = slotEnd;
      } else {
        endUtc = new Date(new Date(slotStart).getTime() + slotDuration * 60000).toISOString();
      }

      handleScheduleQueuedEvent(eventId, slotStart, endUtc, resourceId);
    };

    dropEl.addEventListener('dragover', onDragOver, true);
    dropEl.addEventListener('dragenter', onDragEnter, true);
    dropEl.addEventListener('dragleave', onDragLeave, true);
    dropEl.addEventListener('drop', onDrop, true);

    return () => {
      clearSlot();
      if (ghost.parentNode) document.body.removeChild(ghost);
      dropEl.removeEventListener('dragover', onDragOver, true);
      dropEl.removeEventListener('dragenter', onDragEnter, true);
      dropEl.removeEventListener('dragleave', onDragLeave, true);
      dropEl.removeEventListener('drop', onDrop, true);
    };
  }, [scopeId, queueDragRef, queuedEvents, slotDuration, handleScheduleQueuedEvent]);

  // Always lock Kendo to the explicit startTime/endTime range.
  const workHoursProp = { showWorkHours: false };

  // When showFullDay is true, omit startTime/endTime so Kendo defaults to its
  // built-in full-day range. Passing "00:00"/"00:00" is a zero-length range.
  // The key forces a full remount when toggling so Kendo repaints the time grid.
  const viewTimeProp = showFullDay
    ? {}
    : { startTime: timeRange.start, endTime: timeRange.end };
  const schedulerKey = `${showFullDay}-${timeRange.start}-${timeRange.end}`;

  const schedulerEl = (
    <Scheduler
      key={schedulerKey}
      data={allEventItems}
      date={schedulerDate}
      view={kendoView}
      editable={{
        add: allowEventCreation,
        remove: true,
        drag: true,
        resize: true,
        edit: true,
        select: true,
      }}
      onDateChange={handleDateChange}
      onViewChange={handleViewChange}
      onDataChange={handleDataChange}
      item={ColoredItem}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form={SchedulerFormWrapper as any}
      slot={CustomSlot}
      resources={kendoResources}
      group={kendoGroup}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      footer={footerProp as any}
      height="100%"
    >
      <DayView
        {...viewTimeProp}
        workDayStart={workStart}
        workDayEnd={workEnd}
        {...workHoursProp}
        slotDuration={slotDuration}
        slotDivisions={1}
        dateHeaderCell={dayHeaderCell}
      />
      <WorkWeekView
        {...viewTimeProp}
        workDayStart={workStart}
        workDayEnd={workEnd}
        {...workHoursProp}
        slotDuration={slotDuration}
        slotDivisions={1}
        dateHeaderCell={weekdaysHeaderCell}
      />
      <WeekView
        {...viewTimeProp}
        workDayStart={workStart}
        workDayEnd={workEnd}
        {...workHoursProp}
        slotDuration={slotDuration}
        slotDivisions={1}
        dateHeaderCell={fullWeekHeaderCell}
      />
      <MonthView dateHeaderCell={monthHeaderCell} />
    </Scheduler>
  );

  const pagBtnStyle = (disabled: boolean): React.CSSProperties => ({
    padding: '2px 10px',
    fontSize: 13,
    fontWeight: 600,
    background: 'transparent',
    border: '1px solid #cbd5e1',
    borderRadius: 4,
    cursor: disabled ? 'default' : 'pointer',
    color: disabled ? '#cbd5e1' : '#475569',
    lineHeight: 1.5,
  });

  return (
    <div id={scopeId} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {scopedCss && <style>{scopedCss}</style>}

      {paginationActive && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 12px',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
          flexShrink: 0,
          gap: 8,
        }}>
          <button
            style={pagBtnStyle(resourcePage === 0)}
            disabled={resourcePage === 0}
            onClick={() => setResourcePage((p) => Math.max(0, p - 1))}
            aria-label="Previous resources"
          >
            ‹ Prev
          </button>

          <button
            ref={resourcePickerBtnRef}
            style={{
              fontSize: 12,
              color: '#475569',
              background: showResourcePicker ? '#eff6ff' : 'transparent',
              border: `1px solid ${showResourcePicker ? '#bfdbfe' : '#e2e8f0'}`,
              borderRadius: 4,
              padding: '4px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              minHeight: 32,
              maxWidth: 'min(300px, 50vw)',
              overflow: 'hidden',
            }}
            onClick={() => {
              if (!showResourcePicker && resourcePickerBtnRef.current) {
                setPickerAnchor(resourcePickerBtnRef.current.getBoundingClientRect());
              }
              setShowResourcePicker((v) => !v);
            }}
            aria-haspopup="listbox"
            aria-expanded={showResourcePicker}
            aria-label={`Showing: ${visibleResources?.map((r) => r.name).join(', ')}. Click to jump to a resource.`}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
              {visibleResources?.map((r) => r.name).join(' · ')}
            </span>
            <span style={{ color: '#94a3b8', flexShrink: 0, whiteSpace: 'nowrap' }}>
              {resourcePage + 1}/{totalResourcePages}
            </span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" style={{ flexShrink: 0, opacity: 0.5 }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <button
            style={pagBtnStyle(resourcePage >= totalResourcePages - 1)}
            disabled={resourcePage >= totalResourcePages - 1}
            onClick={() => setResourcePage((p) => Math.min(totalResourcePages - 1, p + 1))}
            aria-label="Next resources"
          >
            Next ›
          </button>
        </div>
      )}

      <div
        ref={schedulerDropRef}
        style={{ flex: 1, minHeight: 0 }}
      >
        <LocalizationProvider language={langKey}>{schedulerEl}</LocalizationProvider>
      </div>

      {aiCreateItem && (
        <CustomEditForm
          dataItem={aiCreateItem}
          isNew
          onClose={() => setAiCreateItem(null)}
          onSubmit={(updatedItem) => {
            const ourEvent = fromKendoItem(updatedItem, resolvedTimezone);
            const { id: _id, ...rest } = ourEvent;
            handleCreateEvent(rest);
            setAiCreateItem(null);
          }}
        />
      )}

      {showResourcePicker && pickerAnchor && createPortal(
        <div
          ref={resourcePickerPopupRef}
          role="listbox"
          aria-label="Jump to resource"
          style={{
            position: 'fixed',
            top: pickerAnchor.bottom + 4,
            left: Math.max(8, Math.min(pickerAnchor.left, window.innerWidth - Math.min(260, window.innerWidth - 16) - 8)),
            width: Math.min(260, window.innerWidth - 16),
            maxHeight: '60vh',
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 99999,
          }}
        >
          <div style={{
            padding: '8px 14px 6px',
            fontSize: 11,
            fontWeight: 600,
            color: '#94a3b8',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            borderBottom: '1px solid #f1f5f9',
            position: 'sticky',
            top: 0,
            background: '#fff',
          }}>
            Jump to Resource
          </div>
          {resources?.map((r, idx) => {
            const page = Math.floor(idx / resourcesPerPage);
            const isCurrentPage = page === resourcePage;
            return (
              <button
                key={r.id}
                role="option"
                aria-selected={isCurrentPage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 14px',
                  fontSize: 13,
                  background: isCurrentPage ? '#eff6ff' : 'transparent',
                  color: isCurrentPage ? '#2563eb' : '#1e293b',
                  fontWeight: isCurrentPage ? 600 : 400,
                  border: 'none',
                  borderBottom: '1px solid #f8fafc',
                  cursor: 'pointer',
                  minHeight: 44,
                  boxSizing: 'border-box',
                }}
                onMouseEnter={(e) => { if (!isCurrentPage) (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if (!isCurrentPage) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                onClick={() => {
                  setResourcePage(page);
                  setShowResourcePicker(false);
                }}
              >
                {r.color && (
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                )}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.name}
                </span>
                {isCurrentPage && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, color: '#2563eb' }}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                <span style={{
                  fontSize: 10,
                  color: isCurrentPage ? '#93c5fd' : '#94a3b8',
                  background: isCurrentPage ? '#dbeafe' : '#f1f5f9',
                  borderRadius: 10,
                  padding: '1px 6px',
                  flexShrink: 0,
                }}>
                  p{page + 1}
                </span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─── Scheduler router ──────────────────────────────────────────────────────
// Chooses between the custom AgendaView and the Kendo-backed views so that
// neither view component needs to handle the other's hook calls.

export function SchedulerView() {
  const { config } = useSchedule();
  return config.view === 'agenda' ? <AgendaView /> : <KendoSchedulerView />;
}
